/** Deployed tiers for portal feature flags: local, dev, preprod, production. Unknown hosts → other. */
export type PlatformEnvKind = 'local' | 'dev' | 'preprod' | 'production' | 'other';

export function detectPlatformEnvKind(flags: {
  isLocal: boolean;
  isDev: boolean;
  isPreprod: boolean;
  isProduction: boolean;
}): PlatformEnvKind {
  if (flags.isLocal) return 'local';
  if (flags.isDev) return 'dev';
  if (flags.isPreprod) return 'preprod';
  if (flags.isProduction) return 'production';
  return 'other';
}

const DEFAULT_COURSE_LIBRARY_SEARCH_BY_KIND: Record<PlatformEnvKind, boolean> = {
  local: true,
  dev: true,
  preprod: true,
  production: true,
  other: false,
};

/**
 * Course library file search on Course Detail (UI / future filtering).
 * Enabled from URL-derived tier only (getPlatformUrlParts); no build-time overrides.
 */
export function isCourseLibrarySearchEnabled(kind: PlatformEnvKind): boolean {
  return DEFAULT_COURSE_LIBRARY_SEARCH_BY_KIND[kind];
}

const DEFAULT_USER_SETTINGS_BY_KIND: Record<PlatformEnvKind, boolean> = {
  local: true,
  dev: true,
  preprod: true,
  production: true,
  other: false,
};

/**
 * Standard-user settings page (/settings). Same URL-tier gating as course library search (no build overrides).
 */
export function isUserSettingsEnabled(kind: PlatformEnvKind): boolean {
  return DEFAULT_USER_SETTINGS_BY_KIND[kind];
}

const DEFAULT_INTERNAL_DEV_TOOLS_BY_KIND: Record<PlatformEnvKind, boolean> = {
  local: true,
  dev: true,
  preprod: false,
  production: false,
  other: false,
};

/**
 * Internal-only tools: AI Recommendation page and organization onboarding test controls.
 * Enabled on localhost and deployed dev only — not staging, preprod, or production.
 */
export function isInternalDevToolsEnabled(kind: PlatformEnvKind): boolean {
  return DEFAULT_INTERNAL_DEV_TOOLS_BY_KIND[kind];
}

/**
 * Client self-service AI assist (Settings “personalize my courses” toggle + gated
 * chat / Change Image AI / translation for client actors).
 * Local + deployed dev only — off in staging/preprod/production (CEE-1208).
 * Spark/Admin Diligent authoring is unaffected (always enabled server-side).
 */
const DEFAULT_CLIENT_AI_ASSIST_SELF_SERVICE_BY_KIND: Record<PlatformEnvKind, boolean> = {
  local: true,
  dev: true,
  preprod: false,
  production: false,
  other: false,
};

export function isClientAiAssistSelfServiceEnabled(kind: PlatformEnvKind): boolean {
  return DEFAULT_CLIENT_AI_ASSIST_SELF_SERVICE_BY_KIND[kind];
}

const DEFAULT_CONTENT_AUTHORING_BY_KIND: Record<PlatformEnvKind, boolean> = {
  local: true,
  dev: true,
  preprod: true,
  production: true,
  other: false,
};

/**
 * Content Authoring portal (/templates). On in every recognised tier — localhost, deployed dev,
 * staging/preprod, and production (CEE-2151); the authoring pipeline deploys prod and eu-prod
 * stages, so the API answers there. Unknown hosts stay off, fail-closed. The flag alone does not
 * grant access — canAccessTemplates still requires super_admin and the Compliance Operations
 * subdomain, which is what keeps Phase 1 to the Spark team.
 */
export function isContentAuthoringEnabled(kind: PlatformEnvKind): boolean {
  return DEFAULT_CONTENT_AUTHORING_BY_KIND[kind];
}

const DEFAULT_COMPLIANCE_EDUCATION_NAV_TREE_BY_KIND: Record<PlatformEnvKind, boolean> = {
  local: true,
  dev: true,
  preprod: true,
  production: true,
  other: false,
};

/**
 * Hierarchical Compliance Education side nav (Content authoring section + aligned children).
 * It is the only route to the authoring pages, so it moves with isContentAuthoringEnabled
 * (CEE-769, CEE-2151).
 */
export function isComplianceEducationNavTreeEnabled(kind: PlatformEnvKind): boolean {
  return DEFAULT_COMPLIANCE_EDUCATION_NAV_TREE_BY_KIND[kind];
}

const DEFAULT_SALES_OPS_AI_BY_KIND: Record<PlatformEnvKind, boolean> = {
  local: true,
  dev: true,
  preprod: true,
  production: true,
  other: false,
};

/**
 * Presales AI recommendation for allowlisted orgs (e.g. sales-ops).
 * Independent of internal dev tools so Sales can use the feature in production.
 */
export function isSalesOpsAiRecommendationEnabled(kind: PlatformEnvKind): boolean {
  return DEFAULT_SALES_OPS_AI_BY_KIND[kind];
}

const DEFAULT_MALWARE_SCANNING_BY_KIND: Record<PlatformEnvKind, boolean> = {
  local: true,
  dev: true,
  preprod: true,
  production: true,
  other: false,
};

/**
 * Malware scanning validation in the UI (blocks download/preview of infected files).
 * Enabled on localhost and deployed dev; other environments pending rollout.
 */
export function isMalwareScanningEnabled(kind: PlatformEnvKind): boolean {
  return DEFAULT_MALWARE_SCANNING_BY_KIND[kind];
}

const DEFAULT_PROMPT_PRIVACY_HINT_BY_KIND: Record<PlatformEnvKind, boolean> = {
  local: false,
  dev: false,
  preprod: false,
  production: false,
  other: false,
};

/**
 * "No personal data or secrets" hint under the AI prompt boxes (CEE-1334). Off in every tier.
 */
export function isPromptPrivacyHintEnabled(kind: PlatformEnvKind): boolean {
  return DEFAULT_PROMPT_PRIVACY_HINT_BY_KIND[kind];
}
